import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { fmtTRY, fmtDate } from "../lib/format";
import { validateUpload } from "../lib/upload";
import { PageHeader } from "../components/Common";
import { useAuth } from "../context/AuthContext";
import { Plus, Trash, PencilSimple, X, Paperclip, UploadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const INCOME_CATS = ["Kira Geliri", "Ek Kilometre Ücreti", "Hasar Tazminatı", "Depozito", "Diğer Gelir"];
const EXPENSE_CATS = ["Yakıt Gideri", "Bakım-Onarım", "Kasko & Sigorta", "Kredi Taksidi", "Personel Maaşı", "Vergi & Harç", "Lastik", "Diğer Gider"];

export default function Transactions({ type }) {
  const isIncome = type === "income";
  const cats = isIncome ? INCOME_CATS : EXPENSE_CATS;
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [plateFilter, setPlateFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [lightbox, setLightbox] = useState(null);
  const [requiredCats, setRequiredCats] = useState([]);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ category: cats[0], amount: "", date: "", description: "", vehicle_plate: "" });

  const load = () => api.get(`/transactions?type=${type}`).then(({ data }) => setItems(data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [type]);
  useEffect(() => {
    api.get("/vehicles").then(({ data }) => setVehicles(data));
    api.get("/settings").then(({ data }) => setRequiredCats(data.receipt_required_categories || []));
  }, []);

  const filtered = items.filter((i) => (plateFilter === "all" || i.vehicle_plate === plateFilter) && (categoryFilter === "all" || i.category === categoryFilter));

  const openNew = () => {
    setEditing(null);
    setForm({ category: cats[0], amount: "", date: new Date().toISOString().slice(0, 10), description: "", vehicle_plate: "", vat_rate: 20, receipt_path: null });
    setModal(true);
  };
  const openEdit = (it) => {
    setEditing(it);
    setForm({ category: it.category, amount: it.amount, date: it.date, description: it.description || "", vehicle_plate: it.vehicle_plate || "", vat_rate: it.vat_rate ?? 20, receipt_path: it.receipt_path || null });
    setModal(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!isIncome && requiredCats.includes(form.category) && !form.receipt_path) {
      toast.error(`'${form.category}' kategorisinde fiş/fatura yüklemek zorunludur`);
      return;
    }
    const payload = { ...form, type, amount: parseFloat(form.amount), vat_rate: parseFloat(form.vat_rate) || 0, vehicle_plate: form.vehicle_plate || null };
    try {
      if (editing) await api.put(`/transactions/${editing.id}`, payload);
      else await api.post("/transactions", payload);
      toast.success(editing ? "Kayıt güncellendi" : "Kayıt eklendi");
      setModal(false);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "İşlem başarısız");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Bu kaydı silmek istediğinize emin misiniz?")) return;
    await api.delete(`/transactions/${id}`);
    toast.success("Kayıt silindi");
    load();
  };

  const [uploading, setUploading] = useState(false);
  const uploadReceipt = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!validateUpload(file, "document")) { e.target.value = ""; return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, receipt_path: data.path }));
      toast.success("Fiş yüklendi");
    } catch {
      toast.error("Yükleme başarısız");
    } finally {
      setUploading(false);
    }
  };

  const viewReceipt = async (path) => {
    try {
      const { data } = await api.get(`/files/${path}`, { responseType: "blob" });
      window.open(URL.createObjectURL(data), "_blank");
    } catch {
      toast.error("Fiş açılamadı");
    }
  };

  const total = filtered.reduce((s, i) => s + i.amount, 0);
  const totalVat = filtered.reduce((s, i) => { const r = i.vat_rate ?? 20; return s + (r ? i.amount - i.amount / (1 + r / 100) : 0); }, 0);
  const cats2 = [...new Set(items.map((i) => i.category))];
  const receiptRequired = !isIncome && requiredCats.includes(form.category);

  return (
    <div className="p-8 max-w-[1400px]">
      <PageHeader
        title={isIncome ? "Gelirler" : "Giderler"}
        subtitle={isIncome ? "Kira ve diğer gelir kalemleri" : "İşletme ve filo giderleri"}
        action={
          <button onClick={openNew} data-testid="add-transaction-button" className="bg-zinc-950 text-white rounded-sm px-4 py-2.5 text-sm font-semibold hover:bg-zinc-800 transition-colors flex items-center gap-2">
            <Plus size={16} weight="bold" /> {isIncome ? "Gelir Ekle" : "Gider Ekle"}
          </button>
        }
      />

      <div className="flex items-center gap-3 mb-4 animate-fade-up">
        <label className="text-xs uppercase tracking-[0.15em] font-semibold text-zinc-500">Araç Filtresi</label>
        <select value={plateFilter} onChange={(e) => setPlateFilter(e.target.value)} data-testid="plate-filter" className="border border-zinc-300 rounded-sm px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
          <option value="all">Tüm Araçlar</option>
          {vehicles.map((v) => <option key={v.id} value={v.plate}>{v.plate} — {v.brand} {v.model}</option>)}
        </select>
        <label className="text-xs uppercase tracking-[0.15em] font-semibold text-zinc-500 ml-2">Kategori</label>
        <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} data-testid="category-filter" className="border border-zinc-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
          <option value="all">Tüm Kategoriler</option>
          {cats2.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        {(plateFilter !== "all" || categoryFilter !== "all") && <button onClick={() => { setPlateFilter("all"); setCategoryFilter("all"); }} data-testid="clear-filters" className="text-xs font-semibold text-zinc-500 hover:text-zinc-900 transition-colors">Temizle</button>}
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4 animate-fade-up">
        <div className="bg-white border border-zinc-200 rounded-sm p-6 flex items-center justify-between">
          <span className="text-xs uppercase tracking-[0.15em] font-semibold text-zinc-500">Toplam {isIncome ? "Gelir" : "Gider"} (KDV Dahil)</span>
          <span className={`font-mono text-2xl font-semibold tabular-nums ${isIncome ? "text-green-600" : "text-red-600"}`} data-testid="transactions-total">{fmtTRY(total)}</span>
        </div>
        <div className="bg-white border border-zinc-200 rounded-sm p-6 flex items-center justify-between">
          <span className="text-xs uppercase tracking-[0.15em] font-semibold text-zinc-500">{isIncome ? "Hesaplanan KDV" : "İndirilecek KDV"}</span>
          <span className="font-mono text-2xl font-semibold tabular-nums text-[#002FA7]" data-testid="transactions-vat">{fmtTRY(totalVat)}</span>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden animate-fade-up" data-testid="transactions-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-200 bg-zinc-50">
              <th className="text-left font-semibold text-zinc-600 px-4 py-3 text-xs uppercase tracking-wider">Tarih</th>
              <th className="text-left font-semibold text-zinc-600 px-4 py-3 text-xs uppercase tracking-wider">Kategori</th>
              <th className="text-left font-semibold text-zinc-600 px-4 py-3 text-xs uppercase tracking-wider">Açıklama</th>
              <th className="text-left font-semibold text-zinc-600 px-4 py-3 text-xs uppercase tracking-wider">Araç</th>
              <th className="text-center font-semibold text-zinc-600 px-4 py-3 text-xs uppercase tracking-wider">KDV</th>
              <th className="text-center font-semibold text-zinc-600 px-4 py-3 text-xs uppercase tracking-wider">Fiş</th>
              <th className="text-right font-semibold text-zinc-600 px-4 py-3 text-xs uppercase tracking-wider">Tutar</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((it) => (
              <tr key={it.id} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors" data-testid={`transaction-row-${it.id}`}>
                <td className="px-4 py-3 font-mono text-zinc-500 text-xs">{fmtDate(it.date)}</td>
                <td className="px-4 py-3 text-zinc-900 font-medium">{it.category}</td>
                <td className="px-4 py-3 text-zinc-500">{it.description || "-"}</td>
                <td className="px-4 py-3 font-mono text-xs text-zinc-500">{it.vehicle_plate || "-"}</td>
                <td className="px-4 py-3 text-center font-mono text-xs text-zinc-500">%{it.vat_rate ?? 20}</td>
                <td className="px-4 py-3 text-center">
                  {it.receipt_path ? <ReceiptThumb path={it.receipt_path} testid={`view-receipt-${it.id}`} onImage={setLightbox} onPdf={viewReceipt} /> : <span className="text-zinc-300">-</span>}
                </td>
                <td className={`px-4 py-3 text-right font-mono font-semibold tabular-nums ${isIncome ? "text-green-600" : "text-red-600"}`}>{fmtTRY(it.amount)}</td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => openEdit(it)} data-testid={`edit-${it.id}`} className="p-1.5 text-zinc-400 hover:text-[#002FA7] transition-colors"><PencilSimple size={16} /></button>
                    <button onClick={() => remove(it.id)} data-testid={`delete-${it.id}`} className="p-1.5 text-zinc-400 hover:text-red-600 transition-colors"><Trash size={16} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-12 text-center text-zinc-400">Henüz kayıt yok.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {modal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setModal(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-white rounded-sm border border-zinc-200 w-full max-w-md p-6 animate-fade-up" data-testid="transaction-modal">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display text-xl font-semibold">{editing ? "Kaydı Düzenle" : (isIncome ? "Yeni Gelir" : "Yeni Gider")}</h3>
              <button type="button" onClick={() => setModal(false)}><X size={20} className="text-zinc-400 hover:text-zinc-900" /></button>
            </div>
            <div className="space-y-4">
              <Field label="Kategori">
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="form-category" className="w-full border border-zinc-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
                  {cats.map((c) => <option key={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Tutar (₺) — KDV Dahil">
                <input type="number" step="0.01" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="form-amount" className="w-full border border-zinc-300 rounded-sm px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
              </Field>
              <Field label="KDV Oranı">
                <select value={form.vat_rate} onChange={(e) => setForm({ ...form, vat_rate: e.target.value })} data-testid="form-vat" className="w-full border border-zinc-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
                  {[0, 1, 10, 20].map((r) => <option key={r} value={r}>%{r}</option>)}
                </select>
              </Field>
              <Field label="Tarih">
                <input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="form-date" className="w-full border border-zinc-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
              </Field>
              <Field label="Araç Plakası (opsiyonel)">
                <input value={form.vehicle_plate} onChange={(e) => setForm({ ...form, vehicle_plate: e.target.value })} data-testid="form-plate" placeholder="34 ABC 123" className="w-full border border-zinc-300 rounded-sm px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
              </Field>
              <Field label="Açıklama">
                <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="form-description" className="w-full border border-zinc-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
              </Field>
              {!isIncome && (
                <Field label={receiptRequired ? "Fatura / Fiş (zorunlu)" : "Fatura / Fiş"}>
                  <label className={`flex items-center gap-2 border rounded-sm px-3 py-2 text-sm text-zinc-600 hover:bg-zinc-50 cursor-pointer ${receiptRequired && !form.receipt_path ? "border-red-400" : "border-zinc-300"}`} data-testid="form-receipt-label">
                    <UploadSimple size={16} weight="bold" />
                    {uploading ? "Yükleniyor..." : form.receipt_path ? "Yüklendi ✓ (değiştir)" : "Görsel / PDF yükle"}
                    <input type="file" accept="image/*,.pdf" onChange={uploadReceipt} data-testid="form-receipt" className="hidden" />
                  </label>
                </Field>
              )}
            </div>
            <button type="submit" data-testid="form-submit" className="w-full mt-6 bg-zinc-950 text-white rounded-sm py-2.5 text-sm font-semibold hover:bg-zinc-800 transition-colors">Kaydet</button>
          </form>
        </div>
      )}

      {lightbox && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] p-6" onClick={() => setLightbox(null)} data-testid="receipt-lightbox">
          <img src={lightbox} alt="fiş" className="max-h-[90vh] max-w-[90vw] object-contain rounded" />
        </div>
      )}
    </div>
  );
}

function ReceiptThumb({ path, testid, onImage, onPdf }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let objurl;
    let alive = true;
    api.get(`/files/${path}`, { responseType: "blob" }).then(({ data }) => {
      if (data.type === "application/pdf") { if (alive) setUrl("pdf"); return; }
      objurl = URL.createObjectURL(data);
      if (alive) setUrl(objurl);
    }).catch(() => {});
    return () => { alive = false; if (objurl) URL.revokeObjectURL(objurl); };
  }, [path]);
  if (url === "pdf") return <button onClick={() => onPdf(path)} data-testid={testid} className="inline-flex text-[#002FA7] hover:opacity-70"><Paperclip size={15} /></button>;
  if (!url) return <span className="text-zinc-300 text-xs">…</span>;
  return <img src={url} alt="fiş" onClick={() => onImage(url)} data-testid={testid} className="w-9 h-9 object-cover rounded border border-zinc-200 cursor-pointer inline-block hover:ring-2 hover:ring-[#002FA7] transition-all" />;
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.15em] font-semibold text-zinc-500 mb-1.5">{label}</label>
      {children}
    </div>
  );
}
